import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, MapPin, Phone, MessageCircle, ChevronDown, CheckCircle2, Heart } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQS = [
  {
    q: "What is Hope Chest?",
    a: "Hope Chest is a digital binder for family caregivers. It keeps medications, appointments, contacts, doctor's notes, daily care logs, photos, and documents all in one place — so you stop hunting through shoeboxes and texts when it matters most.",
  },
  {
    q: "Is Hope Chest free to use?",
    a: "Yes — your first Hope Chest is free forever, with no credit card required. Premium ($14.99/month or $99/year) unlocks unlimited chests, SMS reminders, Google Calendar sync, photo timeline, the printable PDF binder, and family contributor invites.",
  },
  {
    q: "How do I invite my sibling or another family member?",
    a: "Open the Hope Chest, go to the \"Family & care team\" tab, and add their email. They'll get a one-tap invite link. If they already have an account it joins instantly; if not, the chest is waiting for them as soon as they sign up. (Premium feature.)",
  },
  {
    q: "Is my data secure? Is Hope Chest HIPAA compliant?",
    a: "Your data is encrypted in transit and at rest, and we never sell it. Hope Chest is a personal-use tool for families and is not a covered HIPAA entity by default — meaning if you choose to upload health information, you do so as a personal record. For healthcare professionals who need a Business Associate Agreement (BAA), please email us for our Premium business plan.",
  },
  {
    q: "How do I cancel my Premium subscription?",
    a: "Go to Settings → Membership → Manage subscription. You can cancel anytime; your Premium features remain active until the end of your billing period. No questions asked.",
  },
  {
    q: "I'm not getting reminder emails or SMS texts. What do I check?",
    a: "First check your spam folder. Then go to Settings and verify (1) your email notifications are turned on, (2) for SMS, your phone is verified with TCPA opt-in, (3) your individual reminder preferences for appointments / medications / tasks are switched on. If everything looks right, email us — we'll look at the server logs for your account.",
  },
  {
    q: "Can I export or download all my data?",
    a: "Yes. Inside any Hope Chest, use the \"Export PDF\" button to get a printable binder of everything inside. We're also working on a full \"Download all my data\" feature in JSON format — email us if you need it sooner and we'll prioritize it.",
  },
  {
    q: "How do I delete my account?",
    a: "Email support@organizedhope.online from your account address and we'll permanently delete your account and all associated data within 7 days. We'll send you a confirmation when it's done. (A self-service button is coming soon to Settings.)",
  },
];

const TOPICS = [
  { value: "general", label: "General question" },
  { value: "billing", label: "Billing / subscription" },
  { value: "technical", label: "Technical issue / bug" },
  { value: "feature", label: "Feature request" },
  { value: "privacy", label: "Privacy / data request" },
];

function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#EBE5DF] py-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 text-left group"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-display text-lg text-[#3D3935] group-hover:text-[#3A5A4A] transition">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-[#7A736E] mt-1 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 text-[#7A736E] leading-relaxed" data-testid={`faq-answer-${idx}`}>
          {a}
        </p>
      )}
    </div>
  );
}

export default function Support() {
  usePageMeta(
    "Support — Hope Chest",
    "Contact Hope Chest support. Email, phone, FAQs about caregiving, billing, your data, and how to get help. We reply within one business day."
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("general");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in your name, email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/support`, {
        name: name.trim(),
        email: email.trim(),
        topic,
        subject: subject.trim() || undefined,
        message: message.trim(),
      });
      setSubmitted(true);
      toast.success("We got your message — we'll reply within 1 business day.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send right now. Please email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#3D3935]">
      {/* Nav */}
      <nav className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="support-home-link">
          <img src="/hope-chest-logo.png" alt="Hope Chest" className="w-10 h-10 object-contain" />
          <span className="font-brand text-xl font-semibold">Hope Chest</span>
        </Link>
        <div className="flex gap-6 text-sm text-[#7A736E]">
          <Link to="/privacy" className="hover:text-[#3D3935]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#3D3935]">Terms</Link>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-3xl mx-auto px-6 md:px-12 pt-8 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E] mb-3">Support</p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-4">
          We're here when you need us.
        </h1>
        <p className="text-[#7A736E] text-lg leading-relaxed max-w-xl mx-auto">
          Caregiving is hard enough — getting help from us shouldn't be. Send us a note and we'll
          usually reply the same day, never longer than one business day.
        </p>
      </header>

      {/* Contact cards */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-3 gap-4 mb-16">
        <a
          href="mailto:support@organizedhope.online"
          className="hc-card p-6 hover:border-[#3A5A4A] transition group"
          data-testid="support-email-card"
        >
          <div className="w-10 h-10 rounded-full bg-[#D8E2D8] flex items-center justify-center mb-4 group-hover:bg-[#3A5A4A] transition">
            <Mail className="w-5 h-5 text-[#3A5A4A] group-hover:text-white transition" />
          </div>
          <h3 className="font-display text-lg mb-1">Email us</h3>
          <p className="text-sm text-[#7A736E]">support@organizedhope.online</p>
          <p className="text-xs text-[#7A736E] mt-2">Best for detailed questions or when you need to attach a screenshot.</p>
        </a>

        <a
          href="tel:+12565378872"
          className="hc-card p-6 hover:border-[#3A5A4A] transition group"
          data-testid="support-phone-card"
        >
          <div className="w-10 h-10 rounded-full bg-[#E8DCC8] flex items-center justify-center mb-4 group-hover:bg-[#C87B67] transition">
            <Phone className="w-5 h-5 text-[#C87B67] group-hover:text-white transition" />
          </div>
          <h3 className="font-display text-lg mb-1">Call or text</h3>
          <p className="text-sm text-[#7A736E]">(256) 537-8872</p>
          <p className="text-xs text-[#7A736E] mt-2">Mon–Fri, 9am–5pm Central. Leave a message anytime.</p>
        </a>

        <div className="hc-card p-6">
          <div className="w-10 h-10 rounded-full bg-[#F3EFEA] flex items-center justify-center mb-4">
            <MapPin className="w-5 h-5 text-[#7A736E]" />
          </div>
          <h3 className="font-display text-lg mb-1">Mailing address</h3>
          <p className="text-sm text-[#7A736E]">Organized Hope</p>
          <p className="text-sm text-[#7A736E]">2905 Triana Blvd</p>
          <p className="text-sm text-[#7A736E]">Huntsville, AL 35805</p>
        </div>
      </section>

      {/* Contact form + FAQ */}
      <section className="max-w-5xl mx-auto px-6 md:px-12 grid md:grid-cols-[1.1fr_1fr] gap-12 mb-20">
        {/* Form */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <MessageCircle className="w-5 h-5 text-[#3A5A4A]" />
            <h2 className="font-display text-2xl font-semibold">Send us a message</h2>
          </div>

          {submitted ? (
            <div
              className="hc-card p-8 text-center bg-[#D8E2D8]/30 border-[#3A5A4A]/30"
              data-testid="support-success-card"
            >
              <CheckCircle2 className="w-12 h-12 text-[#3A5A4A] mx-auto mb-4" />
              <h3 className="font-display text-xl mb-2">Message sent — thank you.</h3>
              <p className="text-[#7A736E] text-sm leading-relaxed">
                We got it. You'll hear back from <strong>support@organizedhope.online</strong> within one business day,
                usually sooner. Check your spam folder if you don't see a reply.
              </p>
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setName(""); setEmail(""); setSubject(""); setMessage(""); setTopic("general");
                }}
                variant="outline"
                className="mt-6 rounded-full"
                data-testid="support-send-another-btn"
              >
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="support-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sup-name">Your name</Label>
                  <Input
                    id="sup-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Melissa Davis"
                    required
                    data-testid="support-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sup-email">Your email</Label>
                  <Input
                    id="sup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="support-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="sup-topic">What's this about?</Label>
                <select
                  id="sup-topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-[#EBE5DF] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3A5A4A]/20"
                  data-testid="support-topic-select"
                >
                  {TOPICS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="sup-subject">Subject (optional)</Label>
                <Input
                  id="sup-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="A short summary"
                  data-testid="support-subject-input"
                />
              </div>

              <div>
                <Label htmlFor="sup-message">How can we help?</Label>
                <Textarea
                  id="sup-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's going on. The more detail, the faster we can help."
                  rows={6}
                  required
                  data-testid="support-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-[#3A5A4A] hover:bg-[#2C4538] text-white"
                data-testid="support-submit-btn"
              >
                {submitting ? "Sending…" : "Send message"}
              </Button>
              <p className="text-xs text-[#7A736E] text-center">
                We'll only use your email to reply to this message. See our{" "}
                <Link to="/privacy" className="underline">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>

        {/* FAQ */}
        <div>
          <div className="flex items-center gap-2 mb-6">
            <Heart className="w-5 h-5 text-[#C87B67]" fill="#C87B67" />
            <h2 className="font-display text-2xl font-semibold">Common questions</h2>
          </div>
          <div className="hc-card px-6">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      <footer className="max-w-5xl mx-auto px-6 md:px-12 py-12 border-t border-[#EBE5DF] text-sm text-[#7A736E]">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <span>© {new Date().getFullYear()} Organized Hope. Made with care in Huntsville, AL.</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-[#3D3935]">Privacy</Link>
            <Link to="/terms" className="hover:text-[#3D3935]">Terms</Link>
            <Link to="/" className="hover:text-[#3D3935]">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
